import { T } from "./Translate";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  CardMedia,
  Typography,
  Avatar,
  ListItem,
  CircularProgress,
  Button,
  Grid,
  Chip,
  Rating,
  Stack,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Storefront as StorefrontIcon,
  LocalOffer as LocalOfferIcon,
  Event as EventIcon,
  DirectionsCar as DirectionsCarIcon,
  Flight as FlightIcon,
  Group as GroupIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Forum as ForumIcon,
} from "../icons";
import { Chat as ChatIcon } from "../icons";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../session";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { toggleFollow } from "../utils/follow";
import { canMessageUser } from "../utils/messaging";
import BottomToolbar from "./BottomToolbar";
import Search from "./search";
import { formatCount } from "./homeUtils";
import { getHorarioLabel } from "./eventsUtils";
import VerifiedBadge from "./VerifiedBadge";
import { brandColors } from "../theme";
import { pageContentSx } from "../pageLayout";
import useInfiniteScroll from "../hooks/useInfiniteScroll";

import MessagesClosedDialog from "../components/MessagesClosedDialog";
// Etiquetas de los chips "publishedContent" (ver más abajo): sin esto se
// mostraba el nombre interno de la colección tal cual ("offers", "events"),
// no un texto traducido.
const CONTENT_TYPE_LABELS = {
  offers: <T>ofertas</T>,
  events: <T>eventos</T>,
  cars: <T>autos</T>,
  places: <T>propiedades</T>,
};

// Campos por los que se busca en cada categoría (ver getPublicationDisplay
// más abajo, que usa los mismos nombres de campo por colección).
const CATEGORY_SEARCH_KEYS = {
  stores: ["storeName", "name", "description", "address"],
  offers: ["product", "description", "cupon"],
  events: ["title", "description", "direccion"],
  cars: ["title", "marca", "modelo", "description"],
  places: ["title", "description", "city", "category"],
  threads: ["text", "name"],
};
const USER_SEARCH_KEYS = ["displayName", "email", "phone", "point"];

const SEARCH_CATEGORIES = [
  { value: "stores", label: <T>Tiendas</T>, icon: <StorefrontIcon fontSize="small" /> },
  { value: "offers", label: <T>Ofertas</T>, icon: <LocalOfferIcon fontSize="small" /> },
  { value: "events", label: <T>Eventos</T>, icon: <EventIcon fontSize="small" /> },
  { value: "cars", label: <T>Autos</T>, icon: <DirectionsCarIcon fontSize="small" /> },
  { value: "places", label: <T>Propiedades</T>, icon: <FlightIcon fontSize="small" /> },
  { value: "threads", label: <T>Hilos</T>, icon: <ForumIcon fontSize="small" /> },
];

// Publicaciones por tanda en cada pestaña de categoría: se muestran 10 y al
// llegar a la última se suman otras 10 (ver useInfiniteScroll).
const CATEGORY_PAGE_SIZE = 10;

// Ícono de reemplazo (grande, en accent) cuando una publicación no tiene
// imagen: sin esto, el box de imagen (alto fijo para que todas las tarjetas
// midan lo mismo) quedaba como un rectángulo vacío sin nada adentro.
const CATEGORY_ICONS = {
  stores: <StorefrontIcon fontSize="inherit" />,
  offers: <LocalOfferIcon fontSize="inherit" />,
  events: <EventIcon fontSize="inherit" />,
  cars: <DirectionsCarIcon fontSize="inherit" />,
  places: <FlightIcon fontSize="inherit" />,
  threads: <ForumIcon fontSize="inherit" />,
};

// blob: URLs sólo son válidas en la pestaña que las creó; si quedaron
// guardadas en Firestore de una carga anterior, no deben usarse como src
// (ver mismo caso en MyStores.js).
const safeImageUrl = (url) => (url && !url.startsWith("blob:") ? url : null);

const formatShortDate = (value) => {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

// Extrae los datos relevantes de cada publicación según su categoría, ya que
// cada colección de Firestore usa nombres de campo distintos (ej. las
// ofertas guardan el nombre del producto en "product", no en "title" — con
// un único mapeo genérico para todas las categorías, las ofertas terminaban
// mostrando el nombre del vendedor como si fuera el título).
const getDomain = (url) => {
  if (!url) return null;
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// "details" reemplaza al viejo "extra" (una sola línea): cada categoría
// devuelve hasta 3 líneas propias en vez de una genérica, para que la
// tarjeta (alto fijo, ver renderizado) no quede con espacio vacío en
// categorías con pocos campos como tiendas/autos/propiedades.
const getPublicationDisplay = (category, item) => {
  const image =
    safeImageUrl(item.images?.[0]) || safeImageUrl(item.image) || safeImageUrl(item.logoUrl);
  switch (category) {
    case "stores":
      return {
        title: item.storeName || item.name,
        subtitle: item.description,
        price: null,
        rating: item.ratingCount ? { avg: item.avgRating || 0, count: item.ratingCount } : null,
        details: [item.address, item.hours].filter(Boolean),
        image,
      };
    case "offers":
      return {
        title: item.product,
        subtitle: item.description,
        price: item.price != null ? `$${item.price}` : null,
        rating: null,
        details: [
          item.offer ? `${item.offer}% OFF` : null,
          item.cupon ? `Cupón: ${item.cupon}` : null,
          getDomain(item.url),
        ].filter(Boolean),
        image,
      };
    case "events":
      return {
        title: item.title,
        subtitle: item.description,
        price: null,
        rating: null,
        details: [
          formatShortDate(item.date),
          item.direccion,
          [getHorarioLabel(item.horario), item.eventType].filter(Boolean).join(" · "),
        ].filter(Boolean),
        image,
      };
    case "cars":
      return {
        title: item.title || [item.marca, item.modelo].filter(Boolean).join(" "),
        subtitle: item.description,
        price: item.price != null ? `$${item.price}` : null,
        rating: null,
        details: [
          [item.marca, item.modelo, item.año].filter(Boolean).join(" · "),
          item.condition,
          [item.city, item.km ? `${item.km} km` : null].filter(Boolean).join(" · "),
        ].filter(Boolean),
        image,
      };
    case "places":
      return {
        title: item.title,
        subtitle: item.description,
        price: item.price != null ? `$${item.price}` : null,
        rating: null,
        details: [item.category, item.place, item.city].filter(Boolean),
        image,
      };
    case "threads":
      // Sin fallback a item.image: en los hilos es el avatar del autor, no
      // una foto de la publicación.
      return {
        title: item.name,
        subtitle: item.text,
        price: null,
        rating: null,
        details: [
          formatShortDate(item.createdAt),
          `${item.likes?.length || 0} me gusta · ${item.commentsCount || 0} comentarios`,
        ].filter(Boolean),
        image: safeImageUrl(item.images?.[0]),
      };
    default:
      return {
        title: item.title || item.name,
        subtitle: item.description,
        price: item.price != null ? `$${item.price}` : null,
        rating: null,
        details: [item.location || item.point].filter(Boolean),
        image,
      };
  }
};

// Ruta de la publicación puntual para cada categoría. Ofertas y eventos no
// tienen una página de detalle propia (se ven scrolleando el feed), así que
// se linkea al feed con ?highlight=<id>, que scrollea hasta esa card y la
// resalta (ver mismo query param en home.js/events.js).
const getPublicationPath = (category, item) => {
  switch (category) {
    case "stores":
      return item.slug ? `/stores/${item.slug}` : null;
    case "offers":
      return `/home?highlight=${item.id}`;
    case "events":
      return `/events?highlight=${item.id}`;
    case "cars":
      return `/cars/${item.id}`;
    case "places":
      return `/trips/${item.id}`;
    case "threads":
      return `/hilos/${item.id}`;
    default:
      return null;
  }
};

const Members = () => {
  const [messagesClosedOpen, setMessagesClosedOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [following, setFollowing] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [publicationsByCategory, setPublicationsByCategory] = useState({
    offers: [],
    events: [],
    places: [],
    cars: [],
    stores: [],
    threads: [],
  });
  const [brokenImageIds, setBrokenImageIds] = useState(() => new Set());
  const [filteredCategoryItems, setFilteredCategoryItems] = useState([]);
  // Vuelve a la primera tanda al cambiar de pestaña o de búsqueda (cada
  // búsqueda deja un array nuevo en filteredCategoryItems).
  const { limit: categoryLimit, sentinelRef: categorySentinelRef } = useInfiniteScroll(
    filteredCategoryItems,
    CATEGORY_PAGE_SIZE
  );
  const navigate = useNavigate();

  // Bloquea el scroll nativo de la página: en algunos WebViews (Android/PWA)
  // un footer position:fixed puede desincronizarse visualmente del contenido
  // mientras el body scrollea. Al scrollear solo el contenedor interno, el
  // BottomToolbar (fijo al body) nunca necesita "seguir" al scroll.
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;

    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehaviorY = "none";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overscrollBehaviorY = "none";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
    };
  }, []);

  // Ubicación predeterminada: Buenos Aires, Argentina
  const DEFAULT_LOCATION = "Buenos Aires, Argentina";

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const isAuthenticated = await isLoggedIn();
        if (isAuthenticated) {
          setLoading(true);

          const collections = ["offers", "events", "places", "cars"];
          const publishedByUser = {};
          const publications = { offers: [], events: [], places: [], cars: [], stores: [], threads: [] };

          for (const collectionName of collections) {
            // Eventos: solo públicos (las reglas no dejan listar privados ajenos).
            const collectionRef =
              collectionName === "events"
                ? query(collection(db, collectionName), where("isPrivate", "==", false))
                : collection(db, collectionName);
            const collectionSnapshot = await getDocs(collectionRef);

            collectionSnapshot.docs.forEach((docSnap) => {
              const data = docSnap.data();
              if (data.uid) {
                if (!publishedByUser[data.uid]) {
                  publishedByUser[data.uid] = new Set();
                }
                publishedByUser[data.uid].add(collectionName);
                publications[collectionName].push({
                  id: docSnap.id,
                  ownerUid: data.uid,
                  ...data,
                });
              }
            });
          }

          // Buscar propietarios de tiendas (usan ownerId en lugar de uid)
          const storesSnapshot = await getDocs(collection(db, "stores"));
          storesSnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.ownerId) {
              if (!publishedByUser[data.ownerId]) {
                publishedByUser[data.ownerId] = new Set();
              }
              publishedByUser[data.ownerId].add("stores");
              publications.stores.push({
                id: docSnap.id,
                ownerUid: data.ownerId,
                ...data,
              });
            }
          });

          // Hilos: no cuentan como "publicaciones" del directorio de usuarios
          // (publishedByUser), solo se listan en su pestaña. Los reposts se
          // omiten porque duplican el hilo original.
          const threadsSnapshot = await getDocs(collection(db, "threads"));
          threadsSnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.uid && !data.repostOf) {
              publications.threads.push({ id: docSnap.id, ownerUid: data.uid, ...data });
            }
          });
          publications.threads.sort(
            (a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
          );

          setPublicationsByCategory(publications);

          const publishersArray = Object.keys(publishedByUser);

          if (publishersArray.length === 0) {
            setLoading(false);
            return;
          }

          const [userDocs, followerCountDocs, followingCountDocs] = await Promise.all([
            Promise.all(
              publishersArray.map((uid) =>
                // Cuentas protegidas (ver isProtectedOwnerAccount en firestore.rules)
                // rechazan la lectura para quien no sea el dueño/admin; se omiten
                // en vez de tirar abajo la lista completa de publishers.
                getDoc(doc(db, "users", uid)).catch(() => null)
              )
            ),
            Promise.all(
              publishersArray.map((uid) =>
                getDoc(doc(db, "companyFollowers", uid)).catch(() => null)
              )
            ),
            Promise.all(
              publishersArray.map((uid) =>
                getDoc(doc(db, "userFollowing", uid)).catch(() => null)
              )
            ),
          ]);

          const followerCountByUid = {};
          const followingCountByUid = {};
          publishersArray.forEach((uid, index) => {
            const followersSnap = followerCountDocs[index];
            followerCountByUid[uid] =
              followersSnap && followersSnap.exists()
                ? Object.keys(followersSnap.data()).length
                : 0;
            const followingSnap = followingCountDocs[index];
            followingCountByUid[uid] =
              followingSnap && followingSnap.exists()
                ? Object.keys(followingSnap.data()).length
                : 0;
          });

          const usersData = userDocs
            .filter((snap) => snap && snap.exists())
            // Respeta la preferencia "Aparecer en el directorio de miembros"
            // (ajustes de privacidad), salvo para el propio usuario logueado.
            .filter((snap) => snap.data().apareceEnBusqueda !== false || snap.id === auth.currentUser?.uid)
            .map((snap) => {
              const userData = snap.data();
              // El SDK cliente sólo puede leer el email de Auth del propio
              // usuario logueado; para el resto, si no está guardado en su
              // documento de Firestore no hay forma de recuperarlo desde el cliente.
              const email =
                userData.email ||
                (snap.id === auth.currentUser?.uid ? auth.currentUser?.email : null);
              return {
                ...userData,
                uid: userData.uid || snap.id,
                email,
                point: userData.point || DEFAULT_LOCATION,
                publishedContent: Array.from(publishedByUser[snap.id] || []),
                followersCount: followerCountByUid[snap.id] || 0,
                followingCount: followingCountByUid[snap.id] || 0,
              };
            });

          setUsers(usersData);
          setFilteredUsers(usersData);
        }
      } catch (error) {
        console.error("Error obtaining users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchFollowing = async () => {
      try {
        const isAuthenticated = await isLoggedIn();
        if (isAuthenticated && auth.currentUser) {
          const { uid } = auth.currentUser;
          const userFollowingRef = doc(db, "userFollowing", uid);
          const userFollowingDoc = await getDoc(userFollowingRef);

          if (userFollowingDoc.exists()) {
            const followingData = userFollowingDoc.data();
            setFollowing(Object.keys(followingData));
          }
        }
      } catch (error) {
        console.error("Error fetching following:", error);
      }
    };

    fetchFollowing();
  }, []);

  // Cuentas privadas (ver toggleFollow en utils/follow.js): a cuáles le
  // mandé una solicitud de seguimiento que todavía no fue aceptada.
  const [pendingRequestIds, setPendingRequestIds] = useState([]);
  useEffect(() => {
    const fetchPendingRequests = async () => {
      const isAuthenticated = await isLoggedIn();
      if (!isAuthenticated || !auth.currentUser) return;
      const snap = await getDoc(doc(db, "userFollowRequests", auth.currentUser.uid));
      setPendingRequestIds(snap.exists() ? Object.keys(snap.data()) : []);
    };
    fetchPendingRequests();
  }, []);

  const usersByUid = useMemo(
    () => Object.fromEntries(users.map((u) => [u.uid, u])),
    [users]
  );

  const offersCountByUid = useMemo(() => {
    const counts = {};
    publicationsByCategory.offers.forEach((offer) => {
      counts[offer.ownerUid] = (counts[offer.ownerUid] || 0) + 1;
    });
    return counts;
  }, [publicationsByCategory]);

  // Al entrar a una categoría (o al terminar de cargar sus publicaciones) se
  // muestra la lista completa hasta que el usuario tipee algo en el buscador.
  useEffect(() => {
    if (activeCategory && activeCategory !== "all") {
      setFilteredCategoryItems(publicationsByCategory[activeCategory] || []);
    }
  }, [activeCategory, publicationsByCategory]);

  const handleStartDirectChat = async (targetUid) => {
    const isAuthenticated = await isLoggedIn();
    if (!isAuthenticated || !auth.currentUser) {
      navigate("/login");
      return;
    }
    if (!(await canMessageUser(targetUid, auth.currentUser.uid))) {
      setMessagesClosedOpen(true);
      return;
    }
    try {
      const currentUserId = auth.currentUser.uid;
      const sorted = [currentUserId, targetUid].sort();
      const conversationId = `directo_${sorted[0]}_${sorted[1]}`;
      const convRef = doc(db, "conversations", conversationId);
      const snap = await getDoc(convRef);

      if (!snap.exists()) {
        await setDoc(convRef, {
          type: "directo",
          participants: sorted,
          createdAt: serverTimestamp(),
          lastMessageTime: serverTimestamp(),
          unreadCount: { [currentUserId]: 0, [targetUid]: 0 },
        });
      }
      navigate(`/chatbox/directo/${targetUid}`);
    } catch (error) {
      console.error("Error al iniciar el chat:", error);
    }
  };

  const handleFollow = async (companyId) => {
    try {
      const isAuthenticated = await isLoggedIn();
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }

      const { uid, displayName } = auth.currentUser;
      if (!uid) return;

      setLoading(true);
      const result = await toggleFollow({
        currentUid: uid,
        currentDisplayName: displayName,
        targetUid: companyId,
        isFollowing: following.includes(companyId),
        targetIsPrivate: usersByUid[companyId]?.cuentaPrivada === true,
        hasPendingRequest: pendingRequestIds.includes(companyId),
      });
      if (result === "followed") {
        setFollowing([...following, companyId]);
      } else if (result === "unfollowed") {
        setFollowing(following.filter((id) => id !== companyId));
      } else if (result === "requested") {
        setPendingRequestIds([...pendingRequestIds, companyId]);
      } else if (result === "request_cancelled") {
        setPendingRequestIds(pendingRequestIds.filter((id) => id !== companyId));
      }
    } catch (error) {
      console.error("Error handling follow:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickPerfil = (user) => {
    if (user?.uid) {
      navigate(`/profile/${user.uid}`);
    }
  };

  const handleSearchResults = (results) => {
    if (activeCategory && activeCategory !== "all") {
      setFilteredCategoryItems(results);
    } else {
      setFilteredUsers(results);
    }
  };

  const handleClearSearch = () => {
    if (activeCategory && activeCategory !== "all") {
      setFilteredCategoryItems(publicationsByCategory[activeCategory] || []);
    } else {
      setFilteredUsers(users);
    }
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Search
          data={
            activeCategory && activeCategory !== "all"
              ? publicationsByCategory[activeCategory] || []
              : users
          }
          onSearchResults={handleSearchResults}
          onClearSearch={handleClearSearch}
          searchKeys={
            activeCategory && activeCategory !== "all"
              ? CATEGORY_SEARCH_KEYS[activeCategory] || USER_SEARCH_KEYS
              : USER_SEARCH_KEYS
          }
          placeholder={
            activeCategory && activeCategory !== "all"
              ? "Buscar..."
              : "Buscar usuarios..."
          }
          categories={SEARCH_CATEGORIES}
          categoryField="publishedContent"
          onCategoryChange={setActiveCategory}
          noResultsTitle={<T>No se encontraron resultados</T>}
          noResultsMessage={<T>No hay resultados que coincidan con tu búsqueda</T>}
          showBottomToolbar={false}
        />
      </Box>

      <Container
        sx={{
          ...pageContentSx,
          mt: 2,
          mb: 10,
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", color: brandColors.accent, marginTop: 20 }}>
            <CircularProgress />
          </div>
        ) : activeCategory && activeCategory !== "all" ? (
          <Grid container spacing={2}>
            {filteredCategoryItems.slice(0, categoryLimit).map((item) => {
              const display = getPublicationDisplay(activeCategory, item);
              const owner = usersByUid[item.ownerUid];
              const publicationPath = getPublicationPath(activeCategory, item);
              return (
                <Grid item xs={12} sm={6} md={4} key={item.id} sx={{ display: "flex" }}>
                  <ListItem
                    onClick={() => publicationPath && navigate(publicationPath)}
                    sx={{
                      border: `1px solid ${brandColors.divider}`,
                      borderRadius: "8px",
                      padding: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      backgroundColor: brandColors.backgroundPaper,
                      overflow: "hidden",
                      width: "100%",
                      height: 460,
                      cursor: publicationPath ? "pointer" : "default",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 160,
                        flexShrink: 0,
                        backgroundColor: brandColors.backgroundDefault,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {display.image && !brokenImageIds.has(item.id) ? (
                        <CardMedia
                          component="img"
                          image={display.image}
                          alt={display.title}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={() =>
                            setBrokenImageIds((prev) => new Set(prev).add(item.id))
                          }
                        />
                      ) : (
                        <Box sx={{ color: brandColors.accent, opacity: 0.5, fontSize: 48, display: "flex" }}>
                          {CATEGORY_ICONS[activeCategory] || <StorefrontIcon fontSize="inherit" />}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}>
                      <Typography variant="h6" sx={{ color: "text.primary" }} noWrap>
                        {display.title || <T>Sin título</T>}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "3.75em",
                        }}
                      >
                        {display.subtitle || ""}
                      </Typography>

                      {display.price && (
                        <Typography variant="body2" sx={{ fontWeight: "bold", mt: 0.5 }}>
                          {display.price}
                        </Typography>
                      )}

                      {display.rating && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                          <Rating value={display.rating.avg} precision={0.1} readOnly size="small" />
                          <Typography variant="caption" color="text.secondary">
                            {display.rating.avg.toFixed(1)} ({display.rating.count})
                          </Typography>
                        </Stack>
                      )}

                      {display.details.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {display.details.slice(0, 3).map((line, index) => (
                            <Typography key={index} variant="caption" color="text.secondary" display="block" noWrap>
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      <Box sx={{ mt: "auto", pt: 1.5 }}>
                        {owner && owner.uid !== auth.currentUser?.uid && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartDirectChat(owner.uid);
                            }}
                            variant="outlined"
                            color="primary"
                            startIcon={<ChatIcon />}
                            fullWidth
                            sx={{ mb: 1 }}
                          >
                            <T>Enviar mensaje</T>
                          </Button>
                        )}

                        {owner && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFollow(owner.uid);
                            }}
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            fullWidth
                          >
                            {following.includes(owner.uid) ? (
                              <T>Dejar de seguir</T>
                            ) : pendingRequestIds.includes(owner.uid) ? (
                              <T>Solicitud enviada</T>
                            ) : (
                              <T>Seguir</T>
                            )}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                </Grid>
              );
            })}
            {filteredCategoryItems.length > categoryLimit && (
              <Grid item xs={12}>
                <Box ref={categorySentinelRef} sx={{ height: 1 }} />
              </Grid>
            )}
          </Grid>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              <T>Recomendados para ti</T>
            </Typography>
            <Grid container spacing={2}>
            {filteredUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.uid}>
                <ListItem
                  sx={{
                    border: `1px solid ${brandColors.divider}`,
                    borderRadius: "8px",
                    padding: "16px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    backgroundColor: brandColors.backgroundPaper,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Avatar
                        src={user.profileImageUrl}
                        sx={{
                          bgcolor: brandColors.primary,
                          width: 56,
                          height: 56,
                          border: `1.2px solid ${brandColors.divider}`,
                        }}
                      >
                        {!user.profileImageUrl &&
                          (user.displayName ? (
                            user.displayName[0].toUpperCase()
                          ) : (
                            <PersonIcon />
                          ))}
                      </Avatar>
                    </Grid>
                    <Grid item xs>
                      <Button onClick={() => handleClickPerfil(user)}>
                        <Typography variant="h6">
                          {user.displayName || <T>Usuario Desconocido</T>}
                          {user.verified && <VerifiedBadge />}
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {[
                          {
                            icon: <GroupIcon sx={{ fontSize: 16 }} />,
                            value: user.followersCount,
                            label: <T>Seguidores</T>,
                          },
                          {
                            icon: <PersonAddAlt1Icon sx={{ fontSize: 16 }} />,
                            value: user.followingCount,
                            label: <T>Siguiendo</T>,
                          },
                          {
                            icon: <LocalOfferIcon sx={{ fontSize: 16 }} />,
                            value: offersCountByUid[user.uid],
                            label: <T>Ofertas</T>,
                          },
                        ].map((stat, index) => (
                          <Box
                            key={index}
                            sx={{
                              flex: 1,
                              textAlign: "center",
                              py: 0.75,
                              borderRadius: "10px",
                              backgroundColor: brandColors.backgroundDefault,
                              border: `1px solid ${brandColors.divider}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 0.5,
                                color: brandColors.accent,
                              }}
                            >
                              {stat.icon}
                              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                {formatCount(stat.value)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem", lineHeight: 1.4 }}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <LocationIcon
                          fontSize="small"
                          sx={{ mr: 1, verticalAlign: "middle" }}
                        />
                        {user.point || DEFAULT_LOCATION}
                      </Typography>
                    </Grid>
                  </Grid>

                  <div style={{ marginTop: 16, marginBottom: 16 }}>
                    {user.publishedContent?.map((content) =>
                      content === "stores" ? (
                        <Chip
                          key="stores"
                          icon={<StorefrontIcon />}
                          label={<T>tiendas</T>}
                          size="small"
                          color="secondary"
                          sx={{
                            mr: 0.5,
                            mb: 0.5,
                            height: 20,
                            fontSize: "0.7rem",
                            "& .MuiChip-icon": { fontSize: "0.85rem", ml: 0.5 },
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ) : (
                        <Chip
                          key={content}
                          label={CONTENT_TYPE_LABELS[content] || content}
                          size="small"
                          sx={{
                            mr: 0.5,
                            mb: 0.5,
                            height: 20,
                            fontSize: "0.7rem",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      )
                    )}
                  </div>

                  {user.uid !== auth.currentUser?.uid && (
                    <Button
                      onClick={() => handleStartDirectChat(user.uid)}
                      variant="outlined"
                      color="primary"
                      startIcon={<ChatIcon />}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      <T>Enviar mensaje</T>
                    </Button>
                  )}

                  <Button
                    onClick={() => handleFollow(user.uid)}
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    {following.includes(user.uid)
                      ? <T>Dejar de seguir</T>
                      : pendingRequestIds.includes(user.uid)
                      ? <T>Solicitud enviada</T>
                      : <T>Seguir</T>}
                  </Button>
                </ListItem>
              </Grid>
            ))}
            </Grid>
          </>
        )}
        {!loading && (
          <Typography
            variant="caption"
            align="center"
            sx={{ display: "block", color: brandColors.textSecondary, mt: 4, mb: 4 }}
          >
            © 2026 Dapur Tecnología - Notiline
          </Typography>
        )}
      </Container>
      <BottomToolbar />
      <MessagesClosedDialog open={messagesClosedOpen} onClose={() => setMessagesClosedOpen(false)} />
    </Box>
  );
};

export default Members;
